import { useState } from "react";
import { useRole } from "../../../contexts/RoleContext";
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
import LockIcon from "@mui/icons-material/Lock";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import "./Auth.css";

const Auth = () => {
  const { signIn, loading } = useRole();
  const [signingIn, setSigningIn] = useState(false);
  const [error, setError] = useState("");
  const [errorDetails, setErrorDetails] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleCopyError = async () => {
    const textToCopy = [
      `Erro: ${error}`,
      errorDetails ? `\nDetalhes:\n${errorDetails}` : null,
    ]
      .filter(Boolean)
      .join("\n");

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = textToCopy;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (copyErr) {
      console.error("Erro ao copiar erro:", copyErr);
    }
  };

  const handleSignIn = async () => {
    setSigningIn(true);
    setError("");
    setErrorDetails(null);
    setCopied(false);

    try {
      await signIn();
    } catch (err) {
      console.error("Sign in error:", err);

      const details = [
        err.code ? `Código: ${err.code}` : null,
        err.message ? `Mensagem: ${err.message}` : null,
        err.stack ? `Stack:\n${err.stack}` : null,
      ]
        .filter(Boolean)
        .join("\n");

      setErrorDetails(
        details || (typeof err === "object" ? JSON.stringify(err, null, 2) : String(err))
      );

      // Handle specific error cases
      if (err.code === "auth/popup-closed-by-user") {
        setError("Login cancelado pelo usuário.");
      } else if (err.code === "auth/popup-blocked") {
        setError(
          "Pop-up bloqueado pelo navegador. Permita pop-ups para este site."
        );
      } else {
        setError("Erro ao fazer login. Tente novamente.");
      }
    } finally {
      setSigningIn(false);
    }
  };

  if (loading) {
    return (
      <Box className="auth-container">
        <Paper className="auth-card" elevation={3}>
          <Box className="auth-content">
            <CircularProgress size={40} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Verificando autenticação...
            </Typography>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box className="auth-container">
      <Paper className="auth-card" elevation={3}>
        <Box className="auth-content">
          <LockIcon sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />

          <Typography variant="h4" component="h1" gutterBottom>
            Drive Vitor
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 3, textAlign: "center" }}
          >
            Faça login com sua conta Google para acessar seus arquivos
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                width: "100%",
                textAlign: "left",
                "& .MuiAlert-message": { width: "100%" },
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {error}
              </Typography>

              {errorDetails && (
                <Box sx={{ mt: 1 }}>
                  <Box
                    component="pre"
                    sx={{
                      p: 1,
                      m: 0,
                      maxHeight: 140,
                      overflowY: "auto",
                      backgroundColor: "rgba(0, 0, 0, 0.05)",
                      borderRadius: 1,
                      fontSize: "0.75rem",
                      fontFamily: "monospace",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      userSelect: "text",
                    }}
                  >
                    {errorDetails}
                  </Box>

                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={
                      copied ? (
                        <CheckIcon fontSize="small" />
                      ) : (
                        <ContentCopyIcon fontSize="small" />
                      )
                    }
                    onClick={handleCopyError}
                    sx={{
                      mt: 1,
                      fontSize: "0.75rem",
                      textTransform: "none",
                      py: 0.25,
                      px: 1,
                    }}
                  >
                    {copied ? "Copiado!" : "Copiar detalhes do erro"}
                  </Button>
                </Box>
              )}
            </Alert>
          )}

          <Button
            variant="contained"
            size="large"
            onClick={handleSignIn}
            disabled={signingIn}
            startIcon={
              signingIn ? <CircularProgress size={20} /> : <GoogleIcon />
            }
            sx={{
              minWidth: 200,
              py: 1.5,
              fontSize: "1.1rem",
              textTransform: "none",
            }}
          >
            {signingIn ? "Entrando..." : "Entrar com Google"}
          </Button>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 3, textAlign: "center" }}
          >
            Apenas usuários autorizados podem acessar este sistema
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default Auth;
